import {
    ChevronDownIcon,
    UserCircleIcon,
    XCircleIcon,
} from '@heroicons/react/24/outline';
import { MouseEvent, useState } from 'react';
import { cn } from '../../utils';
import { Items, MenuHeaderProps } from './Types';

export function MenuHeader<T extends Items>({
    open,
    error,
    disabled,
    searchInput,
    setOpen,
    textVariant,
    setSearchTerm,
    placeholder,
    selectedItems,
    leadingIcon,
    multiSelect,
    multiSelectVariant,
    doubleLine,
    className,
    setClearSelection,
    allowClear,
    children,
    ...rest
}: MenuHeaderProps<T>) {
    const [hover, setHover] = useState(false);
    const containerClasses = cn(
        'alli-h-11 alli-flex alli-cursor-pointer alli-items-center alli-justify-between alli-border alli-bg-white alli-py-2.5 alli-pl-3 alli-pr-2.5 alli-rounded-lg alli-relative',
        open
            ? 'alli-border-blue-600 sm:alli-shadow-sm'
            : 'alli-border-gray-400',
        error && 'alli-cursor-default alli-border-red-400',
        disabled && 'disabled alli-cursor-not-allowed alli-bg-gray-300',
        searchInput && open && 'alli-pl-0',
        className,
    );
    const selectedItem = selectedItems[0];
    const shouldShowClearIcon = hover && allowClear && selectedItems.length > 0;

    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
        const { target, currentTarget } = event;

        if (
            target instanceof Element &&
            target.closest('[data-testid="select-menu-clear-icon"]')
        ) {
            return;
        }

        if (
            (target === currentTarget ||
                currentTarget.firstChild?.contains(target as Node)) &&
            !disabled
        )
            setOpen(!open);
    };

    const renderChevronIcon = () => (
        <ChevronDownIcon className="alli-h-5 alli-w-5 alli-flex-shrink-0 alli-stroke-2 alli-text-gray-700" />
    );

    const renderXCircleIcon = () => (
        <XCircleIcon
            className="alli-h-5 alli-w-5 alli-flex-shrink-0 alli-stroke-2 alli-text-gray-700"
            onClick={() => {
                setClearSelection(true);
            }}
            data-testid="select-menu-clear-icon"
        />
    );

    const renderInput = () => (
        <input
            autoFocus={true}
            onChange={e => setSearchTerm(e.target.value)}
            onClick={e => e.stopPropagation()}
            placeholder={placeholder}
            className="alli-transition-height alli-w-full alli-resize-none alli-rounded-3xl alli-border-none alli-py-0 alli-text-sm alli-placeholder-gray-700 alli-outline-none alli-duration-300 alli-ease-in-out focus:alli-outline-none focus:alli-ring-0 focus-visible:alli-border-none focus-visible:alli-outline-none focus-visible:alli-ring-0"
            {...rest}
        />
    );

    const renderLeadingIcon = () => (
        <div
            className="alli-h-5 alli-w-5 alli-flex-shrink-0 alli-text-gray-700"
            aria-hidden="true">
            {selectedItem?.icon ? selectedItem.icon : <UserCircleIcon />}
        </div>
    );

    const renderSelectedItem = () => (
        <div
            className="alli-flex alli-w-full alli-items-center alli-gap-3"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            data-testid="selected-item">
            {leadingIcon && renderLeadingIcon()}
            <div
                className={cn(
                    'alli-flex alli-w-full alli-flex-col alli-overflow-hidden',
                    textVariant === 'secondary' &&
                        'alli-flex-row alli-items-center alli-gap-2',
                )}>
                <div
                    title={selectedItem?.title}
                    className={cn(
                        'alli-overflow-hidden alli-text-ellipsis alli-whitespace-nowrap alli-font-normal alli-text-blue-gray-700',
                        doubleLine && 'alli-font-medium',
                    )}>
                    {selectedItem?.title}
                </div>
                {doubleLine && (
                    <div
                        title={`${selectedItem?.id}`}
                        className="alli-overflow-hidden alli-text-ellipsis alli-whitespace-nowrap alli-text-xs alli-font-normal alli-text-gray-700">
                        {selectedItem?.id}
                    </div>
                )}
            </div>
            {shouldShowClearIcon ? renderXCircleIcon() : renderChevronIcon()}
        </div>
    );

    const renderPlaceholder = () => (
        <div
            className={cn(
                'alli-flex alli-w-full alli-items-center alli-justify-between alli-text-sm alli-font-normal',
                selectedItems.length && !open
                    ? 'alli-text-blue-gray-700'
                    : 'alli-text-gray-600',
            )}>
            <div className="alli-overflow-hidden">
                {selectedItems.length ? (
                    multiSelectVariant === 'primary' ? (
                        <div className="alli-flex alli-w-fit alli-max-w-full alli-items-center alli-gap-2">
                            {placeholder}
                            <div
                                className={`alli-flex alli-h-5 alli-items-center alli-justify-center alli-rounded-md  alli-px-1.5 alli-py-0.5 ${disabled ? 'alli-bg-gray-600 alli-text-blue-gray-700' : 'alli-bg-blue-100 alli-text-blue-300'}`}>
                                {selectedItems.length}
                            </div>
                        </div>
                    ) : (
                        selectedItems.length + ' Selected'
                    )
                ) : (
                    <div
                        className="alli-overflow-hidden alli-text-ellipsis alli-whitespace-nowrap"
                        title={placeholder}>
                        {placeholder}
                    </div>
                )}
            </div>
            {renderChevronIcon()}
        </div>
    );

    return (
        <div
            className={containerClasses}
            onClick={handleClick}
            data-testid="selected">
            {searchInput && open ? (
                <div className="alli-flex alli-w-full alli-items-center alli-gap-3">
                    {renderInput()}
                    {renderChevronIcon()}
                </div>
            ) : !multiSelect && selectedItems.length ? (
                renderSelectedItem()
            ) : (
                renderPlaceholder()
            )}
            {children}
        </div>
    );
}
